import React, { useState } from 'react';
import { X, Pin, Hash, Users, MessageSquare, Edit2, Check, BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const ChatChannelPanel = ({ channelInfo, pinnedMessages, onClose, isManager, onSetTopic, onUnpin, channelName }) => {
  const [editingTopic, setEditingTopic] = useState(false);
  const [topicDraft, setTopicDraft] = useState(channelInfo?.topic || '');
  const [activeTab, setActiveTab] = useState('info');

  const handleSaveTopic = () => {
    onSetTopic(topicDraft);
    setEditingTopic(false);
  };

  return (
    <div className="w-80 flex flex-col bg-slate-900/95 border-l border-slate-800 animate-[slideInRight_0.2s_ease-out]" data-testid="channel-panel">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-cyan-400" />
          <h3 className="text-white font-semibold text-sm">{channelName}</h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800">
        {[
          { id: 'info', label: 'Info', icon: BarChart3 },
          { id: 'pinned', label: 'Pinned', icon: Pin, count: pinnedMessages?.length },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-all border-b-2 ${
              activeTab === tab.id ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}>
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {tab.count > 0 && <span className="bg-cyan-500/20 text-cyan-400 text-[10px] px-1.5 rounded-full">{tab.count}</span>}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'info' && (
          <div className="p-4 space-y-4">
            {/* Topic */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Topic</p>
                {isManager && !editingTopic && (
                  <button onClick={() => { setEditingTopic(true); setTopicDraft(channelInfo?.topic || ''); }}
                    className="text-slate-500 hover:text-cyan-400 transition-colors">
                    <Edit2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              {editingTopic ? (
                <div className="space-y-2">
                  <textarea value={topicDraft} onChange={(e) => setTopicDraft(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50 resize-none"
                    rows={2} placeholder="Set a topic..." autoFocus />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditingTopic(false)} className="h-7 text-xs text-slate-400">Cancel</Button>
                    <Button size="sm" onClick={handleSaveTopic} className="h-7 text-xs bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30">
                      <Check className="w-3 h-3 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-slate-400 text-sm">{channelInfo?.topic || 'No topic set'}</p>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Messages', value: channelInfo?.message_count || 0, icon: MessageSquare, color: 'indigo' },
                { label: 'Pinned', value: channelInfo?.pinned_count || 0, icon: Pin, color: 'amber' },
                { label: 'Online', value: channelInfo?.online_members || 0, icon: Users, color: 'emerald' },
                { label: 'Members', value: channelInfo?.total_members || 0, icon: Users, color: 'cyan' },
              ].map(stat => (
                <div key={stat.label} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3">
                  <stat.icon className={`w-4 h-4 text-${stat.color}-400 mb-1`} />
                  <p className="text-white font-bold text-lg">{stat.value}</p>
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'pinned' && (
          <div className="p-2">
            {(!pinnedMessages || pinnedMessages.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                <Pin className="w-10 h-10 text-slate-700 mb-3" />
                <p className="text-slate-500 text-sm">No pinned messages</p>
                <p className="text-slate-600 text-xs mt-1">Pin important messages for quick access</p>
              </div>
            ) : (
              <div className="space-y-1">
                {pinnedMessages.map((msg, i) => (
                  <div key={msg.id || i} className="p-3 rounded-xl bg-slate-800/40 border border-slate-700/30 group">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-indigo-400">{msg.sender_display_name}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-600">{formatTime(msg.timestamp)}</span>
                        {isManager && (
                          <button onClick={() => onUnpin(msg.id)}
                            className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 p-0.5 rounded transition-all">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 line-clamp-3">{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default ChatChannelPanel;
